/**
 * AdminEscritorios — painel do SUPERADMIN (gestor.icit@gmail.com).
 * Gestão de workspaces e dos ativos de cada escritório: logo, nome, modelo
 * timbrado, skills, jurisprudências e arsenal (com duplicação no vault).
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getXEscritorio, setXEscritorio } from '../api';
import { useMe } from '../hooks';

const API = '';
const RAMOS = ['trabalhista', 'civel', 'penal'];
const STATUS = ['trial', 'ativo', 'inadimplente', 'cancelado'];

async function jfetch(path: string, init?: RequestInit) {
  const r = await fetch(API + path, { credentials: 'include', ...init });
  if (!r.ok) throw new Error((await r.json().catch(() => ({})))?.detail ?? `HTTP ${r.status}`);
  return r.json();
}

interface Esc {
  id: number; nome: string; nome_exibicao?: string; ramo: string;
  organizacao?: string; plano: string; status_pagamento: string;
  claude_conectado: number; ativo: number; oab_numero?: string; oab_uf?: string;
}

export function AdminEscritorios() {
  const { data: me } = useMe();
  const [lista, setLista] = useState<Esc[]>([]);
  const [sel, setSel] = useState<Esc | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [arsenal, setArsenal] = useState('');
  const [tokenClaude, setTokenClaude] = useState('');
  // Permissões por capacidade (papel × tenant)
  const [perm, setPerm] = useState<{
    catalogo: Record<string, string[]>;
    papeis: string[];
    overrides: Record<string, string[]>;
  } | null>(null);
  const [novo, setNovo] = useState({ nome: '', ramo: 'trabalhista', gerente_email: '', gerente_nome: '', gerente_senha: '', oab_numero: '', oab_uf: 'RJ' });

  const ehSuper = !!me?.super_admin;

  async function carregar() {
    try { setLista(await jfetch('/api/admin/escritorios')); }
    catch (e: any) { setMsg(e.message); }
  }
  useEffect(() => { if (ehSuper) carregar(); }, [ehSuper]);
  useEffect(() => {
    if (!sel) return;
    jfetch(`/api/admin/escritorios/${sel.id}/arsenal`).then((r) => setArsenal(r.conteudo)).catch(() => setArsenal(''));
    jfetch(`/api/admin/escritorios/${sel.id}/permissoes`).then(setPerm).catch(() => setPerm(null));
  }, [sel?.id]);

  // efetivo de uma capacidade = override (se houver) senão default do catálogo
  function papeisEfetivos(cap: string): string[] {
    if (!perm) return [];
    return perm.overrides[cap] ?? perm.catalogo[cap] ?? [];
  }
  function togglePapel(cap: string, papel: string) {
    if (!perm) return;
    const atual = papeisEfetivos(cap);
    const novo = atual.includes(papel) ? atual.filter((p) => p !== papel) : [...atual, papel];
    setPerm({ ...perm, overrides: { ...perm.overrides, [cap]: novo } });
  }
  async function salvarPermissoes() {
    if (!sel || !perm) return;
    // envia o estado efetivo de TODAS as caps; o backend descarta as iguais ao default
    const overrides: Record<string, string[]> = {};
    for (const cap of Object.keys(perm.catalogo)) overrides[cap] = papeisEfetivos(cap);
    try {
      const r = await jfetch(`/api/admin/escritorios/${sel.id}/permissoes`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ overrides }),
      });
      setPerm({ ...perm, overrides: r.overrides });
      setMsg('✅ permissões salvas');
    } catch (e: any) { setMsg(`❌ ${e.message}`); }
  }

  if (me && !ehSuper) {
    return <div className="p-10 text-center text-sm text-gray-500">Acesso exclusivo do administrador global.</div>;
  }

  async function upload(tipo: string, accept: string) {
    if (!sel) return;
    const input = document.createElement('input');
    input.type = 'file'; input.accept = accept;
    input.onchange = async () => {
      const f = input.files?.[0]; if (!f) return;
      const fd = new FormData(); fd.append('arquivo', f);
      try {
        const r = await jfetch(`/api/admin/escritorios/${sel.id}/${tipo}`, { method: 'POST', body: fd });
        setMsg(`✅ ${tipo}: ${JSON.stringify(r)}`);
      } catch (e: any) { setMsg(`❌ ${tipo}: ${e.message}`); }
    };
    input.click();
  }

  async function patch(campos: Partial<Esc>) {
    if (!sel) return;
    try {
      const r = await jfetch(`/api/admin/escritorios/${sel.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(campos),
      });
      setSel(r); carregar(); setMsg('✅ atualizado');
    } catch (e: any) { setMsg(`❌ ${e.message}`); }
  }

  /** Exclusão DEFINITIVA (irreversível): confirma digitando o nome exato. */
  async function excluirEscritorio() {
    if (!sel || sel.id === 1) return;
    const nome = window.prompt(
      `EXCLUSÃO DEFINITIVA e IRREVERSÍVEL do escritório "${sel.nome}".\n\n` +
      'Isso apaga o registro, o banco de dados, branding, skills e tokens do tenant.\n' +
      'Dica: para só BLOQUEAR sem perder dados, use "🚫 Suspender".\n\n' +
      'Para confirmar, digite o NOME EXATO do escritório:',
    );
    if (nome == null) return;  // cancelou
    if (nome.trim() !== sel.nome.trim()) { setMsg('❌ nome não confere — exclusão cancelada'); return; }
    try {
      const r = await jfetch(`/api/admin/escritorios/${sel.id}?confirmar=${encodeURIComponent(sel.nome)}`, {
        method: 'DELETE',
      });
      // Se você estava visualizando esse escritório (impersonação), sai dele.
      if (getXEscritorio() === String(sel.id)) setXEscritorio(null);
      setMsg(`🗑️ escritório excluído${r?.dir_removido ? ' (dados em disco removidos)' : ''}`);
      setSel(null);
      carregar();
    } catch (e: any) { setMsg(`❌ ${e.message}`); }
  }

  // Estado local dos 2 botões de importar (feedback INLINE, no próprio botão —
  // o `msg` global fica lá no topo da página e passava despercebido: o clique
  // não abre diálogo nenhum como os outros uploads, então sem isso parecia que
  // nada tinha acontecido, mesmo com o backend respondendo 200 corretamente).
  const [statusSkills, setStatusSkills] = useState<'idle' | 'loading' | 'ok' | 'erro'>('idle');
  const [statusJuris, setStatusJuris] = useState<'idle' | 'loading' | 'ok' | 'erro'>('idle');

  /** Re-sincroniza o kit de skills do ramo (KITS_DIR) pra pasta viva do tenant —
   * mesma cópia idempotente do provisionamento, sob demanda. */
  async function importarSkills() {
    if (!sel) return;
    setStatusSkills('loading');
    try {
      const r = await jfetch(`/api/admin/escritorios/${sel.id}/importar-skills`, { method: 'POST' });
      setMsg(r.aviso ? `⚠ skills: ${r.aviso}` : `✅ skills importadas — ${r.arquivos} arquivo(s)`);
      setStatusSkills('ok');
    } catch (e: any) {
      setMsg(`❌ importar skills: ${e.message}`);
      setStatusSkills('erro');
    } finally {
      setTimeout(() => setStatusSkills('idle'), 4000);
    }
  }

  /** Ativa juris_politica=base_curada + juris_autofeed no perfil.json — a mesma
   * configuração que o escritório da Renata já tem, destravando o uso, no
   * pipeline de peças, da curadoria via Falcão (aba Jurisprudência). */
  async function importarJurisprudenciaFalcao() {
    if (!sel) return;
    setStatusJuris('loading');
    try {
      const r = await jfetch(`/api/admin/escritorios/${sel.id}/importar-jurisprudencia-falcao`, { method: 'POST' });
      setMsg(`✅ jurisprudência ativada — política ${r.juris_politica}, autofeed ${r.juris_autofeed ? 'ligado' : 'desligado'}`);
      setStatusJuris('ok');
    } catch (e: any) {
      setMsg(`❌ importar jurisprudência: ${e.message}`);
      setStatusJuris('erro');
    } finally {
      setTimeout(() => setStatusJuris('idle'), 4000);
    }
  }

  async function salvarArsenal() {
    if (!sel) return;
    try {
      const r = await jfetch(`/api/admin/escritorios/${sel.id}/arsenal`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ conteudo: arsenal }),
      });
      setMsg(`✅ arsenal salvo — ${r.teses_no_vault} tese(s) duplicada(s) no vault`);
    } catch (e: any) { setMsg(`❌ arsenal: ${e.message}`); }
  }

  // Conta Claude do tenant selecionado: o superadmin "entra" no workspace
  // via header X-Escritorio (o middleware troca o tenant da request).
  async function conectarClaude() {
    if (!sel || !tokenClaude.trim()) return;
    try {
      await jfetch('/api/escritorio/claude-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Escritorio': String(sel.id) },
        body: JSON.stringify({ token: tokenClaude.trim() }),
      });
      setTokenClaude(''); setMsg('✅ conta Claude conectada'); carregar();
      setSel({ ...sel, claude_conectado: 1 });
    } catch (e: any) { setMsg(`❌ claude: ${e.message}`); }
  }

  async function desconectarClaude() {
    if (!sel) return;
    try {
      await jfetch('/api/escritorio/claude-token', {
        method: 'DELETE', headers: { 'X-Escritorio': String(sel.id) },
      });
      setMsg('✅ conta Claude desconectada'); carregar();
      setSel({ ...sel, claude_conectado: 0 });
    } catch (e: any) { setMsg(`❌ claude: ${e.message}`); }
  }

  function enviarPfx() {
    if (!sel) return;
    const input = document.createElement('input');
    input.type = 'file'; input.accept = '.pfx,.p12';
    input.onchange = async () => {
      const f = input.files?.[0]; if (!f) return;
      const senha = window.prompt('Senha do certificado A1 (.pfx):');
      if (!senha) { setMsg('❌ pfx: senha obrigatória'); return; }
      const fd = new FormData(); fd.append('arquivo', f); fd.append('senha', senha);
      try {
        const r = await jfetch(`/api/admin/escritorios/${sel.id}/pfx`, { method: 'POST', body: fd });
        setMsg(`✅ certificado gravado (${Math.round((r.bytes ?? 0) / 1024)} KB) — busca automatizada habilitada`);
      } catch (e: any) { setMsg(`❌ pfx: ${e.message}`); }
    };
    input.click();
  }

  async function provisionar() {
    try {
      await jfetch('/api/admin/escritorios', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(novo),
      });
      setMsg('✅ workspace provisionado'); setNovo({ ...novo, nome: '', gerente_email: '', gerente_nome: '', gerente_senha: '' });
      carregar();
    } catch (e: any) { setMsg(`❌ provisionar: ${e.message}`); }
  }

  const inputCls = 'text-sm border border-gray-300 dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100';
  const btnCls = 'px-3 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200';

  return (
    <div className="max-w-screen-xl mx-auto p-4 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-xl font-bold text-gray-800 dark:text-gray-100">🏢 Gestão de Escritórios (superadmin)</h1>
        <Link to="/semanal" className="text-sm text-blue-500 hover:underline">← Kanban</Link>
      </div>
      {msg && <div className="text-xs px-3 py-2 rounded bg-blue-50 dark:bg-blue-950/30 text-blue-800 dark:text-blue-200">{msg}</div>}

      {/* Provisionar novo */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-2">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Provisionar novo workspace</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <input className={inputCls} placeholder="Nome do escritório" value={novo.nome} onChange={(e) => setNovo({ ...novo, nome: e.target.value })} />
          <select className={inputCls} value={novo.ramo} onChange={(e) => setNovo({ ...novo, ramo: e.target.value })}>
            {RAMOS.map((r) => <option key={r}>{r}</option>)}
          </select>
          <input className={inputCls} placeholder="Email do gerente" value={novo.gerente_email} onChange={(e) => setNovo({ ...novo, gerente_email: e.target.value })} />
          <input className={inputCls} placeholder="Nome do gerente" value={novo.gerente_nome} onChange={(e) => setNovo({ ...novo, gerente_nome: e.target.value })} />
          <input className={inputCls} placeholder="Senha inicial" value={novo.gerente_senha} onChange={(e) => setNovo({ ...novo, gerente_senha: e.target.value })} />
          <input className={inputCls} placeholder="OAB (número)" value={novo.oab_numero} onChange={(e) => setNovo({ ...novo, oab_numero: e.target.value })} />
          <input className={inputCls} placeholder="UF" value={novo.oab_uf} onChange={(e) => setNovo({ ...novo, oab_uf: e.target.value })} />
          <button onClick={provisionar} className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium">+ Provisionar</button>
        </div>
      </div>

      {/* Lista */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase text-gray-400 border-b border-gray-200 dark:border-gray-700">
            <th className="px-3 py-2">#</th><th className="px-3 py-2">Escritório</th><th className="px-3 py-2">Ramo</th>
            <th className="px-3 py-2">Plano</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Claude</th><th className="px-3 py-2"></th>
          </tr></thead>
          <tbody>
            {lista.map((e) => (
              <tr key={e.id} className={`border-b border-gray-100 dark:border-gray-700/50 ${!e.ativo ? 'opacity-40' : ''} ${sel?.id === e.id ? 'bg-blue-50/50 dark:bg-blue-950/20' : ''}`}>
                <td className="px-3 py-2">{e.id}</td>
                <td className="px-3 py-2 font-medium text-gray-800 dark:text-gray-100">{e.nome_exibicao || e.nome}</td>
                <td className="px-3 py-2">{e.ramo}</td>
                <td className="px-3 py-2">{e.plano}</td>
                <td className="px-3 py-2">{e.status_pagamento}</td>
                <td className="px-3 py-2">{e.claude_conectado ? '🟢' : '⚪'}</td>
                <td className="px-3 py-2"><button onClick={() => setSel(e)} className={btnCls}>Gerir ativos</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Ativos do selecionado */}
      {sel && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-4">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
            Ativos — #{sel.id} {sel.nome_exibicao || sel.nome}
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 items-center">
            <input className={inputCls} defaultValue={sel.nome_exibicao || ''} placeholder="Nome no kanban"
              onBlur={(e) => e.target.value !== (sel.nome_exibicao || '') && patch({ nome_exibicao: e.target.value })} />
            <select className={inputCls} value={sel.ramo} onChange={(e) => patch({ ramo: e.target.value })}>
              {RAMOS.map((r) => <option key={r}>{r}</option>)}
            </select>
            <select className={inputCls} value={sel.plano} onChange={(e) => patch({ plano: e.target.value })}>
              <option>free</option><option>pro</option>
            </select>
            <select className={inputCls} value={sel.status_pagamento} onChange={(e) => patch({ status_pagamento: e.target.value })}>
              {STATUS.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>

          <div className="flex flex-wrap gap-2">
            <button onClick={() => upload('logo', '.png')} className={btnCls}>🖼 Logo (.png)</button>
            <button onClick={() => upload('template', '.docx')} className={btnCls}>📄 Modelo timbrado (.docx)</button>
            <button onClick={() => upload('skills', '.zip')} className={btnCls}>🧩 Skills (.zip)</button>
            <button onClick={() => upload('jurisprudencia', '.md,.zip')} className={btnCls}>⚖️ Jurisprudência (.md/.zip)</button>
            <button onClick={importarSkills} disabled={statusSkills === 'loading'} className={btnCls}
              title="Re-sincroniza as skills do kit do ramo (KITS_DIR) pra pasta do tenant — mesma cópia do provisionamento, sob demanda">
              {statusSkills === 'loading' ? '⏳ Importando…'
                : statusSkills === 'ok' ? '✅ Importado!'
                : statusSkills === 'erro' ? '❌ Falhou (ver msg acima)'
                : '📥 Importar skills (kit)'}
            </button>
            <button onClick={importarJurisprudenciaFalcao} disabled={statusJuris === 'loading'} className={btnCls}
              title="Ativa juris_politica=base_curada + autofeed — mesma configuração do escritório da Renata, destrava o uso da curadoria via Falcão no pipeline de peças">
              {statusJuris === 'loading' ? '⏳ Ativando…'
                : statusJuris === 'ok' ? '✅ Ativado!'
                : statusJuris === 'erro' ? '❌ Falhou (ver msg acima)'
                : '⚖️ Importar jurisprudência (Falcão)'}
            </button>
            <button onClick={enviarPfx} className={btnCls}
              title="Certificado A1 + senha — habilita a busca automatizada de prazos/autos no PJe">
              🔐 Certificado A1 (.pfx)
            </button>
            <button onClick={() => patch({ ativo: sel.ativo ? 0 : 1 } as any)} className={btnCls}>
              {sel.ativo ? '🚫 Suspender' : '✅ Reativar'}
            </button>
            {sel.id !== 1 && (
              <button
                onClick={excluirEscritorio}
                className="px-3 py-2 text-sm rounded-lg border border-red-300/60 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 font-semibold"
                title="Apaga DEFINITIVAMENTE o escritório (registro + banco + arquivos). Irreversível — confirma digitando o nome."
              >
                🗑️ Excluir definitivamente
              </button>
            )}
          </div>

          {/* Conta Claude do escritório */}
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-500 uppercase">
                Conta Claude {sel.claude_conectado ? '— 🟢 conectada' : '— ⚪ não conectada'}
              </span>
              {!!sel.claude_conectado && (
                <button onClick={desconectarClaude} className="px-2 py-1 text-xs rounded border border-red-300/60 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20">
                  Desconectar
                </button>
              )}
            </div>
            {sel.id === 1 ? (
              <p className="text-[11px] text-gray-400">O escritório 1 usa a conta Claude do servidor (não gerida por aqui).</p>
            ) : (
              <>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-relaxed">
                  O dono do escritório roda <code className="bg-gray-100 dark:bg-gray-900 px-1 rounded">claude setup-token</code> na
                  máquina dele (requer assinatura Claude) e envia o token <code>sk-ant-oat…</code>. Cole abaixo:
                </p>
                <div className="flex gap-2">
                  <input value={tokenClaude} onChange={(e) => setTokenClaude(e.target.value)}
                    placeholder="sk-ant-oat01-…" className={`${inputCls} flex-1 font-mono`} type="password" />
                  <button onClick={conectarClaude} disabled={!tokenClaude.trim()}
                    className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm">
                    Conectar
                  </button>
                </div>
              </>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold text-gray-500 uppercase">Arsenal (arsenal.md — duplica no vault ao salvar)</span>
              <button onClick={salvarArsenal} className="px-3 py-1 text-xs rounded bg-blue-600 hover:bg-blue-700 text-white">💾 Salvar + duplicar no vault</button>
            </div>
            <textarea value={arsenal} onChange={(e) => setArsenal(e.target.value)} rows={14}
              className="w-full font-mono text-xs border border-gray-300 dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100" />
          </div>

          {/* 🔐 Permissões por capacidade (papel × tenant) */}
          {perm && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold text-gray-500 uppercase">
                  🔐 Permissões — abas e itens por papel
                </span>
                <button onClick={salvarPermissoes} className="px-3 py-1 text-xs rounded bg-blue-600 hover:bg-blue-700 text-white">💾 Salvar permissões</button>
              </div>
              <p className="text-[11px] text-gray-400 mb-2">
                Marque quais papéis enxergam cada aba/item neste escritório. O superusuário sempre vê tudo.
                Vazio = ninguém (além do superusuário). Em branco no banco = usa o padrão do sistema.
              </p>
              <div className="overflow-x-auto">
                <table className="text-xs border-collapse">
                  <thead>
                    <tr>
                      <th className="text-left px-2 py-1 text-gray-500 font-semibold">Capacidade</th>
                      {perm.papeis.map((p) => (
                        <th key={p} className="px-2 py-1 text-gray-500 font-medium">{p}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(perm.catalogo).map((cap) => {
                      const ef = papeisEfetivos(cap);
                      const customizado = cap in perm.overrides;
                      return (
                        <tr key={cap} className="border-t border-gray-100 dark:border-gray-800">
                          <td className="px-2 py-1 font-mono text-gray-700 dark:text-gray-200 whitespace-nowrap">
                            {cap}{customizado && <span className="ml-1 text-amber-500" title="customizado neste tenant">•</span>}
                          </td>
                          {perm.papeis.map((p) => (
                            <td key={p} className="px-2 py-1 text-center">
                              <input
                                type="checkbox"
                                checked={ef.includes(p)}
                                onChange={() => togglePapel(cap, p)}
                                className="accent-blue-600"
                              />
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
